// Converts strokes to hex regions
// Interprets user's drawn strokes as sets of hexes for rooms

import type { Stroke, StrokePoint, Hex, Point, HexLayout } from "../types.ts";
import {
  pixelToHex,
  hexToKey,
  keyToHex,
  hexLineDraw,
  hexDistance,
  hexNeighbors,
} from "../hex/coordinates.ts";
import { relaxHexRegion, type RelaxOptions } from "./shape-relax.ts";

/**
 * Options for stroke to hex conversion
 */
export interface StrokeToHexOptions {
  relax?: boolean;           // Apply shape relaxation (default: true)
  relaxOptions?: RelaxOptions;
}

const DEFAULT_STROKE_OPTIONS: StrokeToHexOptions = {
  relax: true,
  relaxOptions: {
    fillHoles: true,
    removeSpurs: true,
    smoothness: 0.6,
  },
};

/**
 * Convert a stroke to a hex region (set of hexes)
 * Uses boundary detection + flood fill + shape relaxation
 */
export function strokeToHexRegion(
  stroke: Stroke,
  layout: HexLayout,
  options: StrokeToHexOptions = {}
): Set<string> {
  const opts = { ...DEFAULT_STROKE_OPTIONS, ...options };

  // 1. Get all hexes the stroke passes through (boundary hexes)
  const boundaryHexes = new Set<string>();

  for (let i = 0; i < stroke.points.length - 1; i++) {
    const a = pixelToHex(stroke.points[i], layout);
    const b = pixelToHex(stroke.points[i + 1], layout);
    const lineHexes = hexLineDraw(a, b);

    for (const hex of lineHexes) {
      boundaryHexes.add(hexToKey(hex));
    }
  }

  // 2. Find centroid of stroke
  const centroid = strokeCentroid(stroke);
  const centerHex = pixelToHex(centroid, layout);

  // 3. Flood fill from centroid, bounded by stroke hexes
  let filled = floodFill(centerHex, boundaryHexes, 50);

  // 4. Apply shape relaxation to clean up the region
  if (opts.relax && filled.size >= 3) {
    filled = relaxHexRegion(filled, opts.relaxOptions);
  }

  return filled;
}

/**
 * Flood fill from a starting hex, bounded by boundary hexes
 * Uses breadth-first search
 */
export function floodFill(
  start: Hex,
  boundary: Set<string>,
  maxRadius: number
): Set<string> {
  const filled = new Set<string>();
  const queue: Hex[] = [start];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift()!;
    const key = hexToKey(current);

    // Skip if already visited
    if (visited.has(key)) {
      continue;
    }
    visited.add(key);

    // Stop at boundary (but include boundary in result)
    if (boundary.has(key)) {
      filled.add(key);
      continue;
    }

    // Stop if too far from start
    if (hexDistance(start, current) > maxRadius) {
      continue;
    }

    // Add to filled region
    filled.add(key);

    // Queue neighbors for exploration
    for (const neighbor of hexNeighbors(current)) {
      const neighborKey = hexToKey(neighbor);
      if (!visited.has(neighborKey)) {
        queue.push(neighbor);
      }
    }
  }

  return filled;
}

/**
 * Find the hex in a set that is closest to a given pixel point
 */
export function closestHexToPoint(
  hexes: Set<string>,
  point: Point,
  layout: HexLayout
): Hex {
  let closestHex: Hex | null = null;
  let minDistance = Infinity;

  for (const key of hexes) {
    const hex = keyToHex(key);
    const hexCenter = hexToPixel(hex, layout);
    const distance = Math.hypot(
      hexCenter.x - point.x,
      hexCenter.y - point.y
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestHex = hex;
    }
  }

  // Should never happen if hexes is non-empty, but handle gracefully
  if (closestHex === null) {
    // Return a hex at the point as fallback
    return pixelToHex(point, layout);
  }

  return closestHex;
}

/**
 * Calculate the centroid (center of mass) of a stroke
 * Returns the average position of all points
 */
function strokeCentroid(stroke: Stroke): Point {
  if (stroke.points.length === 0) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;

  for (const point of stroke.points) {
    sumX += point.x;
    sumY += point.y;
  }

  return {
    x: sumX / stroke.points.length,
    y: sumY / stroke.points.length,
  };
}

/**
 * Helper to convert hex to pixel (imported from coordinates but not in import list)
 * This is a local implementation to avoid circular dependencies
 */
function hexToPixel(hex: Hex, layout: HexLayout): Point {
  const SQRT3 = Math.sqrt(3);
  const x = layout.size * (SQRT3 * hex.q + (SQRT3 / 2) * hex.r);
  const y = layout.size * ((3 / 2) * hex.r);
  return {
    x: x + layout.origin.x,
    y: y + layout.origin.y,
  };
}
